/**
 * BDR Tour — the BDR's walk-through.
 * W66.X16 — in-platform guided tour.
 *
 * Role: BDR (the BDR)
 * Tag color: Orange (#EA580C)
 */

import type { GuidedTourStep } from "./types";

export const BDR_TOUR_STEPS: GuidedTourStep[] = [
  {
    stepNumber: 1,
    selector: '[data-tour="bdr-morning-brief"]',
    title: "Step 1 — Morning brief",
    narration:
      "This is where the BDR starts every day. The morning brief shows one-line counts: leads waiting, cadence steps due, SALs accepted this week. No inbox, no guessing — the system tells the BDR exactly where his day begins. This is the daily contract between the BDR and the pipeline.",
    coachingBeat:
      "System prompt: You have 12 cadence steps due today. 3 leads are waiting for your review. Start here.",
    action: "read",
    route: "/#/today",
  },
  {
    stepNumber: 2,
    selector: '[data-tour="bdr-lead-queue"]',
    title: "Step 2 — Lead queue and the hook",
    narration:
      "The lead queue shows the next card: company name, source (where marketing found this person), the date the lead was generated, and the behavior signal — what they did that put them here. the BDR reads the hook before making any call. The hook is the intelligence briefing in one sentence. Without it, the BDR is guessing.",
    coachingBeat:
      "System signal: This lead was sourced from a LinkedIn campaign targeting CFOs at mid-market manufacturers. Generated 4 days ago. Visited the pricing page twice. That is a warm signal — prioritize.",
    action: "read",
    route: "/#/today",
  },
  {
    stepNumber: 3,
    selector: '[data-tour="bdr-accept-recycle-reject"]',
    title: "Step 3 — Accept, Recycle, Reject",
    narration:
      "Three buttons. This is the most important click in the BDR workflow. Accept to SAL moves the lead into the sales-accepted pipeline. Recycle puts it back in the nurture queue with a note. Reject kills it with a reason code. Every click feeds the MQL-to-SAL waterfall math. When the BDR clicks Reject, marketing sees exactly where leads die. This is marketing honesty — the system holds everyone accountable to the same standard.",
    coachingBeat:
      "Why this click matters: Marketing tracks the accept rate. If the BDR rejects more than 40% of leads from a specific source, that source's cost-per-SAL is recalculated. the BDR's discipline here directly improves the quality of leads he gets next quarter.",
    action: "click",
    route: "/#/today",
  },
  {
    stepNumber: 4,
    selector: '[data-tour="bdr-cadence-log"]',
    title: "Step 4 — Cadence step auto-logging",
    narration:
      "When the BDR completes a cadence step — a call, an email, a LinkedIn touch — the system logs it automatically. No manual entry. The cadence engine knows which step is next, how many days until the follow-up window opens, and whether this prospect has gone quiet. the BDR focuses on the conversation, not on CRM hygiene.",
    coachingBeat:
      "System note: Step 3 of 7 logged. Next step opens in 2 business days: follow-up call. The system will remind you.",
    action: "read",
    route: "/#/today",
  },
  {
    stepNumber: 5,
    selector: '[data-tour="bdr-truth-mirror"]',
    title: "Step 5 — The truth mirror (footer dials)",
    narration:
      "At the bottom of the BDR's dashboard are the truth-mirror dials: his own activity counts versus the team average. Calls made. Emails sent. SALs accepted. These are not vanity numbers — they are the honest comparison between what the BDR is doing and what the team is doing. If the team average is above the BDR's line, that is a coaching signal the manager can see too.",
    coachingBeat:
      "Coaching prompt: the BDR's cadence completion rate is 71% vs team average of 84%. This is a coaching flag. The gap closes when the BDR works the full queue, not just the easy leads.",
    action: "read",
    route: "/#/today",
  },
  {
    stepNumber: 6,
    selector: '[data-tour="bdr-afternoon-receipt"]',
    title: "Step 6 — Afternoon receipt",
    narration:
      "By end of day, the receipt view shows the final count: how many cadence steps the BDR worked out of how many were scheduled. Nine of twelve means three leads did not get touched today. The system does not forget them — they carry forward to tomorrow with priority flags. The receipt creates daily accountability without requiring a manager to chase the BDR. The system is the accountability partner.",
    coachingBeat:
      "End-of-day summary: 9 of 12 cadence steps completed. 3 steps carried forward. 2 new leads added to tomorrow's queue. Your accept rate today: 100% (2 of 2). Strong.",
    action: "read",
    route: "/#/today",
  },
];
